import React from "react";
import Axios from "axios";
import moment from "moment";
import { message } from "antd";
import { antdAsyncMessageError, antdAsyncMessageSuccess } from "./AsyncMessage";
import {
  IconEE,
  IconENG,
  IconFI,
  IconLT,
  IconLV,
} from "../components/Layout/Icons";

export const getDateFormat = (date) => {
  var d = moment(date);
  return date && d.isValid() ? d : "";
};

export function validateEmail(email) {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const customAntdMessage = (title, body, type = "info") => {
  const config = {
    content: (
      <div className="custom-ant-message-content">
        <div className="custom-ant-message-title">{title}</div>
        <div className="custom-ant-message-body">{body}</div>
      </div>
    ),
    className: "custom-ant-message-notice",
    duration: 3,
  };

  switch (type) {
    case "success":
      message.success(config);
      break;
    case "error":
      message.error(config);
      break;
    case "warning":
      message.warning(config);
      break;
    default:
      message.info(config);
      break;
  }
};

export const antdMessageInfo = (text) => {
  message.info(text || "In progress");
};

export const antdMessageSuccess = (text) => {
  message.success(text || "Successfully completed");
};

export const antdMessageError = (text) => {
  const safe =
    text && typeof text === "object"
      ? Object.values(text).flat().join(", ")
      : text;
  message.error(safe || "Error occurred");
};

export const colorBlockShipment = (item) => {
  if (item.failed_delivery_at) {
    return "badge badge-pill badge-danger text-xs px-2 font-normal";
  } else if (item.picked_up_at && !item.delivered_at) {
    return "badge badge-pill badge-warning text-xs px-2 font-normal";
  } else if (item.delivered_at) {
    return "badge badge-pill badge-success text-xs px-2 font-normal";
  } else {
    return "badge badge-pill badge-light text-xs px-2 font-normal";
  }
};

export const phonenumberValid = (inputtxt) => {
  if (!inputtxt) {
    return false;
  }
  return true;
  const inputStarts = inputtxt.startsWith("+370");
  if (!inputStarts) {
    return false;
  }
  const inputLength = inputtxt.split("+370")[1].trim();
  if (inputLength.length === 8) {
    let validAccess = true;
    inputLength.split("").forEach((item) => {
      if (isNaN(Number(item))) {
        validAccess = false;
      }
    });

    if (validAccess) {
      return true;
    } else {
      return false;
    }
  }
  return false;
};

const PHONE_SPECS = {
  LT: {
    min: 8,
    max: 8,
    startsWith: "6",
    label: "Lithuanian",
    example: "61234567",
  },
  LV: { min: 8, max: 8, label: "Latvian", example: "12345678" },
  EE: { min: 7, max: 8, label: "Estonian" },
  PL: { min: 9, max: 9, label: "Polish" },
  FI: { min: 9, max: 9, label: "Finnish" },
};
const PHONE_DEFAULT_SPEC = { min: 7, max: 9 };
const PHONE_CALLING_CODES = {
  LT: "370",
  LV: "371",
  EE: "372",
  PL: "48",
  FI: "358",
};

const phoneMatchesSpec = (value, spec) => {
  if (!/^\d+$/.test(value)) return false;
  if (value.length < spec.min || value.length > spec.max) return false;
  if (spec.startsWith && !value.startsWith(spec.startsWith)) return false;
  return true;
};

const phoneDigitRange = (spec) =>
  spec.min === spec.max
    ? `${spec.min} digits`
    : `${spec.min}–${spec.max} digits`;

export const phonenumberErrorByCountry = (phone, country) => {
  const code = String(country || "").toUpperCase();
  const spec = PHONE_SPECS[code] || PHONE_DEFAULT_SPEC;
  if (!phone || !String(phone).trim()) return "Phone number is required";

  let value = String(phone).replace(/[\s\-()]/g, "");
  let internationalForm = false;
  if (value.startsWith("+")) {
    value = value.slice(1);
    internationalForm = true;
  } else if (value.startsWith("00")) {
    value = value.slice(2);
    internationalForm = true;
  }

  const callingCode = PHONE_CALLING_CODES[code];
  let candidates;
  if (internationalForm) {
    const local =
      callingCode && value.startsWith(callingCode)
        ? value.slice(callingCode.length)
        : value;
    candidates = [local];
  } else {
    candidates = [value];
    if (callingCode && value.startsWith(callingCode)) {
      candidates.push(value.slice(callingCode.length));
    }
  }
  if (candidates.some((c) => phoneMatchesSpec(c, spec))) return "";

  const local = candidates[candidates.length - 1];
  const who = spec.label ? `${spec.label} phone number` : "Phone number";
  const range = phoneDigitRange(spec);
  const example = spec.example ? ` (e.g. ${spec.example})` : "";
  if (!/^\d+$/.test(local)) {
    return `${who} must contain digits only — got "${local}"`;
  }
  if (spec.startsWith && !local.startsWith(spec.startsWith)) {
    return `${who} must start with ${spec.startsWith} and be ${range}${example} — got "${local}"`;
  }
  if (local.length < spec.min) {
    return `${who} is too short: ${local.length} digits, needs ${range}${example} — got "${local}"`;
  }
  if (local.length > spec.max) {
    return `${who} is too long: ${local.length} digits, needs ${range}${example} — got "${local}"`;
  }
  return `${who} must be ${range}${example}`;
};

export const phonenumberValidByCountry = (phone, country) =>
  phonenumberErrorByCountry(phone, country) === "";

// First/last name fields accept letters (latin, incl. lithuanian diacritics),
// a hyphen and a space only. Everything else is stripped while typing.
const DISALLOWED_NAME_CHARS =
  /[^A-Za-zÀ-ÖØ-öø-ɏḀ-ỿ\- ]/g;

export const sanitizePersonName = (value) =>
  String(value || "")
    .replace(/\s/g, " ")
    .replace(DISALLOWED_NAME_CHARS, "");

export const emailValid = (str) => {
  const pattern = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  return pattern.test(str);
};

export const toFixedNumber = (number) => {
  const inspection = +number || 0;
  return inspection.toFixed(2);
};

export const exportDocument = (url, docName, method = "GET", data = {}) => {
  return Axios({
    url: url,
    method,
    data,
    responseType: "blob", // important
    headers: { Accept: "application/vnd.ms-excel" },
  })
    .then((response) => {
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = downloadUrl;

      link.setAttribute("download", docName); //any other extension

      document.body.appendChild(link);

      link.click();

      link.remove();
      // antdMessageSuccess(`Successfully downloading ${docName}`);
      antdAsyncMessageSuccess({
        key: "exportDocument",
        text: `Successfully downloading ${docName}`,
      });
      return { success: true };
    })
    .catch((err) => {
      errorHandler(err);
      // antdMessageError();
      antdAsyncMessageError({
        key: "exportDocument",
      });
      return { success: false };
    });
};

export function getUrlExtension(url) {
  try {
    return url
      .split("/")
      .pop()
      .match(/^[\w\s-,.]+\.([a-zA-Z0-9]{3,4})/)[1];
  } catch (ignored) {
    return false;
  }
}

export const formatDateMinus = (date) =>
  moment(date).subtract(1, "days").format("YYYY-MM-DD");

export const formatDatePlus = (date) =>
  moment(date).add(1, "days").format("YYYY-MM-DD");

// Moments

export const prettyDateFormat = (date) => {
  if (date) {
    return moment(date).format("YYYY-MM-DD HH:mm");
  }
  return moment().format("YYYY-MM-DD HH:mm");
};

export function makeid(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

export const parcelTerminalBulder = (items) => {
  const parcels = {
    omniva: {},
    dpd: {},
    lp_express: {},
    venipak: {},
    posti: {},
    matkahuolto: {},
  };

  Object.entries(items).forEach(([key, value]) => {
    const values = {};
    // Just in case;
    if (!parcels[key]) return (parcels[key] = {});
    Object.entries(value).forEach(([key, parcels]) => {
      parcels.forEach((parcel) => {
        if (!values[key]) values[key] = [];
        values[key].push({
          // ...value,
          country_code: key,
          value: parcel.id,
          label:
            key == "omniva"
              ? parcel.title
              : [parcel.title, parcel.address, parcel.city]
                  .filter(Boolean)
                  .join(", "),
        });
      });
    });
    parcels[key] = values;
  });

  return parcels;
};

export const returnEuroString = (number) => {
  const num = +number || 0;
  if (num == 0) return 0 + " €";
  return numberRound(num) + " €";
};

export const returnToOffice = (
  { billing_address = {}, carrier = {} },
  pickup_addresses = [],
) => {
  let buildAddress = {
    ...billing_address,
    company: "",
    firstname: "UAB Electronic trade",
    lastname: "UAB Electronic trade",
    street: "Veiverių g. 171, Kaunas",
    city: "Kaunas",
    phone: "+37067377579",
    postal_code: "46422",
    parcel_terminal_id: null,
    other: "info@bigbox.lt",
    country_id: 122,
  };
  if (!!carrier?.pickup_address_id && pickup_addresses) {
    const current_pickup_addresses = pickup_addresses.find(
      (pickup_address) => pickup_address.id == carrier.pickup_address_id,
    );
    if (current_pickup_addresses) {
      buildAddress = {
        ...buildAddress,
        firstname: "Verslo vartai - Technorama",
        lastname: "Verslo vartai - Technorama",
        street: current_pickup_addresses.address,
        city: current_pickup_addresses.city,
        phone: current_pickup_addresses.phone,
        postal_code: current_pickup_addresses.postal_code,
        other: current_pickup_addresses.email,
        country_id: current_pickup_addresses.country_id,
      };
    }
  }
  return buildAddress;
};

export function getFromPercentage(percent, total) {
  return (percent / 100) * total;
}

export function getPercentageChange(oldNumber, newNumber) {
  const decreaseValue = oldNumber - newNumber;
  const total = (decreaseValue / oldNumber) * 100;
  return total.toString().startsWith("-")
    ? `-${getPercentageChange(newNumber, oldNumber)}`
    : total;
}

export function relDiff(a, b) {
  if (isNaN(+a) || isNaN(+b)) {
    return "";
  }
  return a - b === 0 ? 0 : 100 * Math.abs((a - b) / b) || "";
}

export const numberRound = (number) => {
  number = +number;
  if (!number) return "0";
  return number.toFixed(2).replace(".00", "");
};

export const url_to_store_by_reference = (store_url, ref) => {
  const url = store_url
    ? `https://${store_url.toLowerCase()}`
    : "https://bigbox.lt";

  return `https://bigbox.lt/product/${ref}-product-title`;
};
export const first_element_array = (arr) => {
  if (Array.isArray(arr)) {
    if (arr.length > 0) {
      return arr[0];
    }
  }
  return false;
};

export const errorHandler = (err) => {
  console.log("ERR ST--");
  console.error(err);
  console.log("ERR EN--");
};

export const shopCategoryMainNames = (id, name, mainId) => {
  const ids = [6602, 3427, 5909, 3439, 16685];
  if (!ids.includes(id)) return "";
  switch (id) {
    case 6602:
      if (mainId == 11170) {
        return `${name} FI`;
      } else {
        return `${name} LT`;
      }
    case 3427:
      return `${name} LT`;
    case 5909:
      return `${name} EE`;
    case 3439:
      return `${name} EE`;
    case 16685:
      return `${name} LV`;
    default:
      return "";
  }
};

export const buildErrorObject = (obj) => {
  if (typeof obj !== "object") return {};
  let errors = {};
  Object.entries(obj).forEach((d) => {
    const error_string = d[1].join(", ").toUpperCase();
    antdMessageError(error_string);
    return (errors[d[0]] = error_string);
  });
  return errors;
};

export const log = (string) => {
  console.count("Log call");
  console.log(string);
};

export function copyToClipboard(text) {
  const elem = document.createElement("textarea");
  elem.value = text;
  document.body.appendChild(elem);
  elem.select();
  document.execCommand("copy");
  document.body.removeChild(elem);
  antdMessageSuccess(`Copied text: ${text}`);
}

export function paginate(array, page_size, page_number) {
  return array.slice((page_number - 1) * page_size, page_number * page_size);
}

export const uniqueArray = (arr) => [...new Set(arr)];

export function getBase64(img, callback) {
  const reader = new FileReader();
  reader.addEventListener("load", () =>
    callback({ name: img.name, url: reader.result }),
  );
  reader.readAsDataURL(img);
}

export const flag_icons = {
  LT: <IconLT />,
  LV: <IconLV />,
  EE: <IconEE />,
  ENG: <IconENG />,
  FI: <IconFI />,
};
